import * as PIXI from "pixi.js";
import { fireCoinToLogo } from "../Shared/coinToLogoTrail";
import TickerTimeout from "../../utils/TickerTimeout";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { formatCoinValue } from "../Shared/coinValueLabel";
// import { HOLD_AND_WIN_ROWS, HOLD_AND_WIN_BASE_ROWS } from "../../RGS/Providers/PopiPlay/holdAndWinState";
import { isScreenRowOpen } from "../../RGS/Providers/PopiPlay/holdAndWinState";
import { paced, trailGapMs } from "./holdAndWinPace";

/**
 * chestCollection — the chests collecting, inside ONE respin (GDD "Resolve
 * Collectors", "Chest", "Golden Chest").
 *
 * After the respin's landings, every collector that collects this step does,
 * one at a time — Pot Collection's collect (PotCollectionFeatureExecutor.
 * executeCollect), on this game's fire trail:
 *
 *   one by one   each valued cell in the active zone, in reading order, sends
 *                its fire trail into the chest; on arrival the chest's value
 *                counts up by that cell's value (the first arrival opens it:
 *                `open` -> `open_idle`)
 *   a press      the remaining trails fire together, and the chest lands on
 *                its value — the same skip as Pot Collection's slam stop
 *   the end      the chest is set to the SERVER's value, whatever the count
 *                reached — the count is presentation, the number is theirs
 *
 * The source cells KEEP their values — a chest copies the zone total, it never
 * takes it (GDD: "The operation copies value. It does not empty the board.").
 * Values leave the board only in the End Collector.
 *
 * ── Who collects this step (contract §5.2) ──
 *
 *   new_cells      a chest / golden chest that LANDED in the zone and collected
 *                  comes here with its collected value (> 0). A pending chest
 *                  (locked row) lands with 0 and does not collect.
 *   updated_cells  chests that collected WITHOUT landing: a golden chest
 *                  recollecting (its own old value counts), a pending chest
 *                  whose row just opened.
 *
 * ── Order ──
 *
 * The server's: data row 0 first, left to right — the order its values were
 * computed in, each collector seeing the previous one's NEW value. Animated in
 * any other order, a chest would show a total that already counts a chest the
 * player has not seen collect yet. (GDD says "bottom to top"; contract §14.5 —
 * re-check when the API is aligned.)
 *
 * Every delay runs on the ticker — never window timers.
 */

const COLLECTOR_KINDS = new Set(["chest", "golden_chest"]);
// The chest's number counting up to each arrival, ms (Pot Collection: half
// its 300 ms travel).
const COUNT_MS = 150;
// After a chest has collected, before the next collector starts, ms.
// Replaced by the end of the chest's current `open_idle` pass (untilOrSkip
// below) — the clip's own timing, not a number.
// const AFTER_COLLECT_MS = 350;
// Between one collector and the next — Pot Collection's ACTIVATION_DELAY
// (PotCollectionOrbActivator), skippable, and never after the last one.
const BETWEEN_COLLECTORS_MS = 300;
// ── The H&W trails' own curve (2026-10-06) ── gentler than the logo throw's
// and scaled with the distance (fireCoinToLogo `arc`): the arc's control
// rises `height` × the horizontal distance (+ `lift` symbol heights); a
// (nearly) vertical trail swings out `sideBow` × the vertical distance. A
// quadratic peaks halfway to its control, so the drawn bend is half these.
const HNW_TRAIL_ARC = { height: 0.3, lift: 0.05, sideBow: 0.5 };
// Longest one trail is waited on before moving on anyway, ms.
const TRAIL_TIMEOUT_MS = 1500;

// /** Screen row -> data row (the inverse of holdAndWinState.toScreenRow). */
// export const toDataRow = (screenRow) =>
//   screenRow >= HOLD_AND_WIN_BASE_ROWS
//     ? screenRow - HOLD_AND_WIN_BASE_ROWS
//     : HOLD_AND_WIN_ROWS - 1 - screenRow;
/** Screen row -> data row: the same row since 2026-10-14 (holdAndWinState). */
export const toDataRow = (screenRow) => screenRow;

/** The collectors of this step, in the server's order. */
export const collectorsOf = (step) => {
  if (!step) return [];
  const landed = (step.newCells || []).filter(
    (cell) => COLLECTOR_KINDS.has(cell.kind) && cell.value > 0,
  );
  const updated = step.updatedCells || [];
  // return [...landed.map((c) => ({ ...c, landed: true })), ...updated]
  //   .sort((a, b) => a.row - b.row || a.reel - b.reel);
  // ── GDD order: "Left to right → next row → bottom to top" (2026-10-06) ──
  // On SCREEN: the bottom row first, left to right in each row. The data-row
  // sort ran the first four rows top to bottom. The simulator resolves in the
  // same order (dmon-rgsim collectorRowOrder) — the two MUST agree, or the
  // running totals shown during a collection differ from what each chest took.
  return [...landed.map((c) => ({ ...c, landed: true })), ...updated]
    .sort((a, b) => b.screenRow - a.screenRow || a.reel - b.reel);
};

/**
 * The values to LAND with: the server's grid, except a collector landing this
 * step lands EMPTY — it gets its value when it collects.
 */
export const landingValues = (values, step) => {
  const landing = collectorsOf(step).filter((c) => c.landed);
  if (!landing.length || !values) return values;
  const out = values.map((column) => (Array.isArray(column) ? column.slice() : column));
  landing.forEach(({ reel, screenRow }) => {
    if (out[reel]) out[reel][screenRow] = 0;
  });
  return out;
};

// The longest untilOrSkip waits, ms — far past any landing or reveal it
// covers (~1 s), only there so a lost clip cannot hold the round.
const UNTIL_OR_SKIP_SAFETY_MS = 6000;

/** Resolves when `pred()` is true, or after `maxMs`. Checked every frame. */
export const waitUntil = (pred, maxMs) =>
  new Promise((resolve) => {
    let elapsed = 0;
    const tick = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      if (pred() || elapsed >= maxMs) {
        PIXI.Ticker.shared.remove(tick);
        resolve();
      }
    };
    PIXI.Ticker.shared.add(tick);
  });

/**
 * Resolves when `promise` settles, or at once when the player presses skip —
 * one frame check, ended by whichever comes first, nothing left behind.
 */
export const untilOrSkip = (promise, isSkipped) => {
  let done = false;
  Promise.resolve(promise).then(() => {
    done = true;
  });
  // return waitUntil(() => done || isSkipped(), Number.POSITIVE_INFINITY);
  // ── Never for ever (2026-10-08, the Zeus c11dfce9 hole) ── a reveal or a
  // landing whose clip was replaced without its `end` left the respins
  // waiting for a press. Released after UNTIL_OR_SKIP_SAFETY_MS regardless.
  return waitUntil(() => done || isSkipped(), UNTIL_OR_SKIP_SAFETY_MS);
};

/**
 * One fire trail, as a promise that resolves on arrival (or a timeout).
 * `carry` rides its head (fireCoinToLogo).
 */
/**
 * ── A jackpot coin shows its VALUE before it is collected ──
 * (Vladimir, 2026-10-01: Pot Collection turns a coin before it collects it.)
 * Every source that is still a jackpot coin on its tier word plays
 * `double_value` — the word shrinks away, its money value pops in — all at
 * once; resolves when they are up (or on a skip). Coins a scarab or an earlier
 * collection already turned are left alone. Used by the chest collections and
 * the End Collector, right before their trails fly.
 */
export const revealHiddenJackpots = (overlay, sources, isSkipped) => {
  const reveals = (sources || [])
    .filter(({ c, r }) => overlay?.isHiddenJackpot?.(c, r))
    .map(({ c, r, v }) => overlay.revealJackpotValue(c, r, v, { withWin: false }));
  if (!reveals.length) return Promise.resolve();
  return untilOrSkip(Promise.all(reveals), isSkipped);
};

/**
 * ── ALL AT ONCE (Vladimir, 2026-10-06) ── the moment a collection starts (a
 * chest's, or the End Collector's), every jackpot coin about to be collected
 * that still shows its tier word flips to its value TOGETHER — then the
 * trails collect them like any coin. One at a time, a board with several
 * jackpot coins took ages. Awaited in full, a press included (Pot
 * Collection's rule: a skip never cuts the flip — see revealBeforeCollect).
 */
export const flipJackpotsAtOnce = (overlay, sources) => {
  const flips = (sources || [])
    .filter(({ c, r }) => overlay?.isHiddenJackpot?.(c, r))
    .map(({ c, r, v }) => overlay.revealJackpotValue(c, r, v, { withWin: false }));
  return flips.length ? Promise.all(flips) : Promise.resolve();
};

/**
 * ── ITS turn: a jackpot coin turns, THEN it is collected ── (Vladimir,
 * 2026-10-01: "when it's the jackpot coin's turn to get collected … just
 * before we collect it, not at the start"). Called by each trail right before
 * it flies; resolves at once for any other cell. A press does not wait for it.
 */
// eslint-disable-next-line no-unused-vars
export const revealBeforeCollect = (overlay, { c, r, v }, isSkipped) => {
  if (!overlay?.isHiddenJackpot?.(c, r)) return Promise.resolve();
  const reveal = overlay.revealJackpotValue(c, r, v, { withWin: false });
  // return isSkipped?.() ? Promise.resolve() : untilOrSkip(reveal, isSkipped);
  // ── A skip never cuts the flip (2026-10-05, Pot Collection's rule) ──
  // Not waited on a press, the trail fired on the same frame, took the value
  // and played `darken` over `double_value` — the coin was collected without
  // ever turning. PotCollectionEndOfBonusEngine flips every jackpot coin and
  // AWAITS it before any trail, fast mode included; a skip only makes the
  // trails fly together.
  return reveal;
};

// `light`: the LIGHT trail profile — for trails flown as a burst (a skip).
// `targetGlobal`: a point, or a function returning one — called as the trail
// leaves, so a trail after a resize flies to where the target is NOW.
export const flyTrail = (layer, from, targetGlobal, carry = null, onProgress = null, light = false) =>
  new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      timeout.clear();
      resolve();
    };
    const timeout = new TickerTimeout(finish, TRAIL_TIMEOUT_MS);
    // fireCoinToLogo({ layer, from, target: targetGlobal, onArrival: finish, carry, onProgress, light });
    // bowVertical: a cell straight above / below its target swings out to one
    // side on every orientation, as the portrait logo throw does (2026-10-06).
    fireCoinToLogo({
      layer,
      from,
      target: typeof targetGlobal === "function" ? targetGlobal() : targetGlobal,
      onArrival: finish,
      carry,
      onProgress,
      light,
      bowVertical: true,
      arc: HNW_TRAIL_ARC,
      // The chest / End Collector trails: under the chest's collect (2026-10-22).
      sound: SOUND_NAME.hold_and_win_trail,
      // // Turbo flies them faster (holdAndWinPace, 2026-10-23); 1 = as authored.
      // style: collectTimeScale() < 1 ? { durationMs: paced(TRAIL_FLIGHT_MS()) } : null,
      // (Reverted 2026-10-23: "trails always look the same" — turbo shortens
      // the GAPS between them, never their flight.)
    });
  });

/** Count `from` -> `to` over `ms` on the ticker, calling `apply(n)`. */
export const countUp = (from, to, ms, apply) =>
  new Promise((resolve) => {
    let elapsed = 0;
    const tick = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(1, elapsed / ms);
      apply(Math.round(from + (to - from) * t));
      if (t >= 1) {
        PIXI.Ticker.shared.remove(tick);
        resolve();
      }
    };
    PIXI.Ticker.shared.add(tick);
  });

/**
 * Play this step's collections.
 *
 * @param {object}   p
 * @param {object}   p.step          holdAndWin.step (newCells, updatedCells)
 * @param {Array}    p.values        the server's grid now, screen rows
 * @param {Array}    p.prevValues    the grid before this response (a golden
 *                                   chest's old value), screen rows
 * @param {number}   p.unlockedRows  the step's active rows (data rows 0..n-1)
 * @param {object}   p.overlay       HoldAndWinOverlay's handle
 * @param {object}   [p.trailLayer]  where the trails fly — above the whole
 *                                   room (the controller's trailLayer)
 * @param {Function} p.isSkipped     true once the player pressed to skip
 * @returns {Promise<void>}
 */
export const playChestCollections = async ({
  step,
  values,
  prevValues,
  unlockedRows,
  overlay,
  trailLayer,
  isSkipped,
}) => {
  const collectors = collectorsOf(step);
  if (!collectors.length || !overlay || !values) return;

  // ── Every landing of this step plays out first ──
  //
  // The reels stopping is not the landing: each new cell still plays its `in`
  // (a coin its `win` too) before it settles into its idle. A chest that
  // started collecting on the stop was barely seen arriving. Wait on the
  // spines themselves — no timer. A press skips the wait, as it skips the
  // trails.
  // The CHESTS' landings only (Vladimir, 2026-09-29: "im talking about CHEST
  // not coins") — a coin's `in` -> `win` is long and is not waited on. A chest
  // that landed this step goes the moment its idle starts; one that did not
  // land (a golden chest recollecting, a pending chest whose row opened) has
  // nothing to wait for.
  // await untilOrSkip(
  //   Promise.all(
  //     (step.newCells || []).map(({ reel, screenRow }) =>
  //       overlay.landed?.(reel, screenRow) ?? Promise.resolve(),
  //     ),
  //   ),
  //   isSkipped,
  // );
  await untilOrSkip(
    Promise.all(
      collectors
        .filter((c) => c.landed)
        .map(({ reel, screenRow }) =>
          overlay.landed?.(reel, screenRow) ?? Promise.resolve(),
        ),
    ),
    isSkipped,
  );

  // The grid as the player sees it: final values, except each collector at
  // what it held BEFORE collecting (a landed chest 0, a golden chest its old
  // value). Updated as each one collects.
  const running = values.map((column) => (Array.isArray(column) ? column.slice() : column));
  collectors.forEach(({ reel, screenRow, landed }) => {
    running[reel][screenRow] = landed ? 0 : prevValues?.[reel]?.[screenRow] ?? 0;
  });
  // const inZone = (screenRow) => toDataRow(screenRow) < unlockedRows;
  const inZone = (screenRow) => isScreenRowOpen(screenRow, unlockedRows);

  for (const collector of collectors) {
    const { reel, screenRow, value } = collector;
    const target = overlay.cellAnchor(reel, screenRow);
    const layer = {
      trailContainer: trailLayer ?? overlay.trailLayer(),
      // Read as each trail leaves — the current cell size after a resize.
      get symbolHeight() {
        return overlay.cellHeight?.();
      },
    };

    // Its sources: every valued cell in the zone but itself, reading order
    // (top row first, left to right — Pot Collection's sortGridOrder).
    const sources = [];
    for (let r = 0; r < running[0].length; r++) {
      for (let c = 0; c < running.length; c++) {
        const v = running[c][r];
        if (v > 0 && inZone(r) && !(c === reel && r === screenRow)) sources.push({ c, r, v });
      }
    }
    // // Jackpot coins turn into their values first (revealHiddenJackpots).
    // await revealHiddenJackpots(overlay, sources, isSkipped);
    // — no: each turns on ITS turn (revealBeforeCollect, in flyFrom).
    // ── ...all at once again, as the collection starts (2026-10-06) ──
    // flipJackpotsAtOnce; revealBeforeCollect in flyFrom is then a no-op.
    await flipJackpotsAtOnce(overlay, sources);

    if (target && layer.trailContainer && sources.length) {
      // Where the chest is as each trail leaves (a resize moves it).
      const targetGlobal = () => target.toGlobal(new PIXI.Point(0, 0));
      let total = running[reel][screenRow];
      // ── The chest opens as the collection starts ──
      // Not awaited any more: waiting out the whole `open` held the round
      // after the landing (Vladimir, 2026-09-29: "we wait eternity after
      // that"). The lid starts opening and the first trail leaves together,
      // the moment the chest's idle has started.
      // await untilOrSkip(
      //   overlay.collect(reel, screenRow, total, isSkipped()),
      //   isSkipped,
      // );
      overlay.collect(reel, screenRow, total, isSkipped());
      // ── Pot Collection's arrival: the count runs, the next trail does not
      // wait for it ── (executeCollect: _animateCounter per arrival, then
      // _waitForCounters once at the end.)
      const counters = [];
      // Its collect sound, per value dropping in (batch 3, 2026-10-22).
      const collectSound =
        overlay.skin?.(reel, screenRow) === "gold"
          ? SOUND_NAME.gold_chest_collect
          : SOUND_NAME.silver_chest_collect;
      // ── Never past the server's number (2026-11-03) ── the server caps a
      // chest at the round's max win; the zone may hold more. Only the cells up
      // to (and including) the one that reaches `value` fly, and the count is
      // held to it — the End Collector's capped rule. Uncapped, the cells add
      // up to `value` exactly and every one flies.
      let flying = sources;
      if (value > 0) {
        let planned = total;
        const last = sources.findIndex(({ v }) => {
          planned += v;
          return planned >= value;
        });
        if (last > -1) flying = sources.slice(0, last + 1);
      }
      const arrive = (v) => {
        // The chest jiggles as the value drops in (its `collect` clip).
        overlay.playCollect?.(reel, screenRow);
        // ...and is heard — once per frame, so a skipped burst is one hit.
        soundManager.playOncePerFrame(collectSound);
        const before = total;
        // total += v;
        total = value > 0 ? Math.min(total + v, value) : total + v;
        counters.push(
          // countUp(before, total, COUNT_MS, (n) => overlay.setValue(reel, screenRow, n)),
          countUp(before, total, paced(COUNT_MS), (n) => overlay.setValue(reel, screenRow, n)),
        );
      };
      // ── The value TRAVELS, but the coin KEEPS it ──
      // Each trail carries a copy of its cell's number into the chest, as the
      // End Collector's do — but a chest COPIES (GDD), so the coin's own
      // number stays and the coin does not darken. The chest's number is the
      // same font at the same size, so the copy simply keeps the coin
      // number's on-screen size the whole way — nothing to resize.
      const worldScaleY = (obj) => {
        const wt = obj?.worldTransform;
        return wt ? Math.hypot(wt.c, wt.d) : 0;
      };
      // const flyFrom = (src) => {
      // `light`: one of a skipped burst — the LIGHT trail.
      const flyFrom = async (src, light = false) => {
        // A jackpot coin turns into its value first — on its own turn.
        await revealBeforeCollect(overlay, src, isSkipped);
        const coinLabel = overlay.valueLabel?.(src.c, src.r);
        let carried = null;
        if (coinLabel?._fontSize > 0) {
          carried = createCoinLabel();
          // setCoinLabel(carried, formatCoinValue(src.v), coinLabel._fontSize, 0);
          // In the coin's own colours (a jackpot coin's tier).
          setCoinLabel(
            carried,
            formatCoinValue(src.v),
            coinLabel._fontSize,
            0,
            overlay.valueStyle?.(src.c, src.r),
          );
          carried.scale.set(
            worldScaleY(coinLabel) / (worldScaleY(layer.trailContainer) || 1),
          );
        }
        return flyTrail(
          layer,
          overlay.cellAnchor(src.c, src.r),
          targetGlobal,
          carried,
          null,
          light,
        ).then(() => arrive(src.v));
      };

      // ── Turbo: one by one, NOT waiting for each other (2026-10-23) ── each
      // trail leaves TRAIL_GAP_MS after the one before, at its own unchanged
      // speed; a skip still sends the rest together.
      const gap = trailGapMs();
      const flights = [];
      // for (let i = 0; i < sources.length; i++) {
      for (let i = 0; i < flying.length; i++) {
        if (isSkipped()) {
          // The rest together — Pot Collection's slam stop — on LIGHT trails:
          // a chest takes the whole zone, and up to 39 full trails at once
          // lagged (Vladimir, 2026-10-01: "make trails lightweight").
          // await Promise.all(sources.slice(i).map(flyFrom));
          // await Promise.all(sources.slice(i).map((src) => flyFrom(src, true)));
          await Promise.all(flying.slice(i).map((src) => flyFrom(src, true)));
          break;
        }
        // await flyFrom(sources[i]);
        if (gap == null) {
          await flyFrom(flying[i]);
        } else {
          flights.push(flyFrom(flying[i]));
          if (i < flying.length - 1) await waitUntil(isSkipped, gap);
        }
      }
      await Promise.all(flights);
      // The last count finishes before the chest lands on the server's value.
      await Promise.all(counters);
    } else {
      overlay.collect(reel, screenRow, value, isSkipped());
    }

    // The server's number, always.
    overlay.setValue(reel, screenRow, value);
    running[reel][screenRow] = value;
    // A silver chest collects ONCE — done, it shuts (a golden one stays open
    // until its last collection). Vladimir, 2026-10-06.
    // overlay.closeIfSpent?.(reel, screenRow);
    // ...and a golden one after its LAST collection — the respin that settles
    // the round, before the scarabs (2026-10-06: done its purpose, it shuts).
    overlay.closeIfSpent?.(reel, screenRow, step?.phase === "settled");

    // ── Room to breathe ──
    // The open chest's `open_idle` runs to the end of its current pass before
    // the next collector (or the next respin) — cut short by a press.
    // if (!isSkipped()) await waitUntil(isSkipped, AFTER_COLLECT_MS);
    // Dropped — waiting out the idle made the round drag (Vladimir,
    // 2026-09-29: "i never said that idle should finish").
    // if (!isSkipped()) {
    //   await untilOrSkip(overlay.idleLoopEnd?.(reel, screenRow), isSkipped);
    // }
    // Pot Collection's gap between activations, and only BETWEEN collectors.
    const isLast = collector === collectors[collectors.length - 1];
    if (!isLast && !isSkipped()) {
      // await waitUntil(isSkipped, BETWEEN_COLLECTORS_MS);
      await waitUntil(isSkipped, paced(BETWEEN_COLLECTORS_MS));
    }
  }
};
